import { useCallback, useEffect, useMemo, useState } from "react";

import ErrorNotice from "@/components/ErrorNotice";
import { getGithubConflict, resolveGithubConflict } from "@/lib/api";
import { describeGithubError, type DescribedError } from "@/lib/github-errors";
import type { ConflictFile, ConflictResolutionFile, GithubConflictDetail } from "@/lib/types";

// Git-style 3-way conflict resolver. Non-conflicting files are auto-merged server-side; the owner
// only edits the conflicting hunks (text) or picks a side (binary). Submitting creates a
// `github-merge` review suggestion and hands its id back via onResolved so the caller can send the
// owner straight to that suggestion's review pipeline.
export default function ConflictResolverModal({
  slug,
  accessToken,
  onClose,
  onResolved,
}: {
  slug: string;
  accessToken: string;
  onClose: () => void;
  onResolved: (suggestionId: string) => void;
}) {
  const [detail, setDetail] = useState<GithubConflictDetail | null>(null);
  const [loadError, setLoadError] = useState<DescribedError | null>(null);
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [choices, setChoices] = useState<Record<string, "ours" | "theirs" | "delete">>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<DescribedError | null>(null);

  useEffect(() => {
    let active = true;
    getGithubConflict(slug, accessToken)
      .then((d) => {
        if (!active) return;
        setDetail(d);
        // Pre-fill text-conflict editors with the diff3 merged preview (markers included).
        const seed: Record<string, string> = {};
        for (const f of d.files) if (f.status === "conflict" && f.merged != null) seed[f.path] = f.merged;
        setEdits(seed);
      })
      .catch((e) => active && setLoadError(describeGithubError(e, "conflict")));
    return () => {
      active = false;
    };
  }, [slug, accessToken]);

  const conflicts = (detail?.files ?? []).filter((f) => f.conflicted);
  const auto = (detail?.files ?? []).filter((f) => !f.conflicted);

  // Every conflicting file must be fully decided before the button activates: a text file with no
  // leftover `<<<<<<<`/`>>>>>>>` markers, a binary/structural file with a side chosen. (No conflicts
  // to decide — all auto-merged — is trivially "resolved", so the button stays enabled.)
  const allResolved = conflicts.every((f) =>
    f.status === "conflict"
      ? !/^(<<<<<<<|>>>>>>>)/m.test(edits[f.path] ?? f.merged ?? "")
      : !!choices[f.path],
  );

  const submit = useCallback(async () => {
    if (!detail) return;
    // Guard: every conflicted hunk must be decided (no leftover markers) before we send it.
    const unresolved = conflicts.find(
      (f) => f.status === "conflict" && /^(<<<<<<<|>>>>>>>)/m.test(edits[f.path] ?? f.merged ?? ""),
    );
    if (unresolved) {
      setError({
        tone: "error",
        title: "Finish resolving first",
        detail: `Resolve every conflicting block in ${unresolved.path} before submitting.`,
      });
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const files: ConflictResolutionFile[] = conflicts.map((f) =>
        f.status === "conflict"
          ? { path: f.path, content: edits[f.path] ?? "" }
          : { path: f.path, action: choices[f.path] ?? "theirs" },
      );
      const { suggestion_id } = await resolveGithubConflict(slug, files, detail.head_sha, accessToken);
      onResolved(suggestion_id);  // caller redirects to that suggestion's review pipeline
    } catch (e) {
      setError(describeGithubError(e, "resolve"));
      setBusy(false);
    }
  }, [detail, conflicts, edits, choices, slug, accessToken, onResolved]);

  return (
    <div className="fixed inset-0 z-[100] overflow-y-auto bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="flex min-h-full items-start justify-center p-6 pt-16">
        <div
          className="w-full max-w-6xl rounded-2xl border border-[var(--border)] bg-[var(--bg-elev)] shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between border-b border-[var(--border)] px-6 py-4">
            <h2 className="font-display text-base font-semibold text-text">Resolve GitHub conflict</h2>
            <button
              onClick={onClose}
              className="flex h-7 w-7 items-center justify-center rounded-lg text-text/40 transition hover:bg-black/[0.05] hover:text-text dark:hover:bg-white/10"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
            </button>
          </div>

          <div className="px-6 py-5" style={{ maxHeight: "70vh", overflowY: "auto" }}>
            {loadError ? (
              <ErrorNotice error={loadError} />
            ) : !detail ? (
              <p className="text-sm text-text/45">Loading the three-way diff…</p>
            ) : (
              <div className="space-y-5">
                <p className="text-sm text-text/55">
                  {conflicts.length} file{conflicts.length === 1 ? "" : "s"} need your attention;{" "}
                  {auto.length} auto-merged. Each clashing block shows the{" "}
                  <b className="text-amber-600 dark:text-amber-300">Platform</b> and <b className="text-blue-600 dark:text-blue-300">GitHub</b> lines —{" "}
                  <b>Accept</b> one per block (or Keep both). Non-conflicting lines are kept automatically.
                </p>

                {conflicts.map((f) => (
                  <ConflictFileEditor
                    key={f.path}
                    file={f}
                    value={edits[f.path] ?? ""}
                    onText={(v) => setEdits((p) => ({ ...p, [f.path]: v }))}
                    choice={choices[f.path]}
                    onChoice={(c) => setChoices((p) => ({ ...p, [f.path]: c }))}
                  />
                ))}

                {auto.length > 0 && (
                  <details className="rounded-lg border border-[var(--border)] bg-[var(--glass)] px-3 py-2">
                    <summary className="cursor-pointer text-xs text-text/50">
                      {auto.length} auto-merged file{auto.length === 1 ? "" : "s"}
                    </summary>
                    <ul className="mt-2 space-y-1">
                      {auto.map((f) => (
                        <li key={f.path} className="text-xs text-text/50">
                          <span className="font-mono text-text/70">{f.path}</span>{" "}
                          <span className="text-text/40">
                            ({f.status === "auto_ours" ? "platform" : f.status === "auto_theirs" ? "GitHub" : "both"})
                          </span>
                        </li>
                      ))}
                    </ul>
                  </details>
                )}

                <ErrorNotice error={error} />
              </div>
            )}
          </div>

          {detail && !loadError && (
            <div className="flex items-center justify-end gap-2 border-t border-[var(--border)] px-6 py-4">
              <button onClick={onClose} disabled={busy} className="rounded-lg border border-[var(--border)] px-4 py-2 text-sm text-text/70 hover:bg-black/[0.04] dark:hover:bg-white/5 disabled:opacity-50">
                Cancel
              </button>
              <button
                onClick={submit}
                disabled={busy || !allResolved}
                title={allResolved ? undefined : "Resolve every conflict block first"}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {busy ? "Processing…" : "Process suggestion"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

type HunkChoice = "ours" | "theirs" | "both";
type Seg =
  | { kind: "text"; lines: string[] }
  | { kind: "conflict"; ours: string[]; theirs: string[] };

// Parse diff3 merged text (with markers) into context segments + conflict hunks.
function parseSegments(merged: string): Seg[] {
  const lines = merged.split("\n");
  const segs: Seg[] = [];
  let text: string[] = [];
  const flush = () => {
    if (text.length) segs.push({ kind: "text", lines: text });
    text = [];
  };
  let i = 0;
  while (i < lines.length) {
    if (lines[i].startsWith("<<<<<<<")) {
      flush();
      i++;
      const ours: string[] = [];
      while (i < lines.length && lines[i] !== "=======") ours.push(lines[i++]);
      i++; // skip =======
      const theirs: string[] = [];
      while (i < lines.length && !lines[i].startsWith(">>>>>>>")) theirs.push(lines[i++]);
      i++; // skip >>>>>>>
      segs.push({ kind: "conflict", ours, theirs });
    } else {
      text.push(lines[i++]);
    }
  }
  flush();
  return segs;
}

// Rebuild the file from segments + per-hunk choices; an unresolved hunk keeps its markers so the
// submit guard can block until every conflict is decided.
function buildResolved(segs: Seg[], choices: (HunkChoice | undefined)[]): string {
  const out: string[] = [];
  let h = 0;
  for (const s of segs) {
    if (s.kind === "text") {
      out.push(...s.lines);
      continue;
    }
    const c = choices[h++];
    if (c === "ours") out.push(...s.ours);
    else if (c === "theirs") out.push(...s.theirs);
    else if (c === "both") out.push(...s.ours, ...s.theirs);
    else out.push("<<<<<<< ours (platform)", ...s.ours, "=======", ...s.theirs, ">>>>>>> theirs (GitHub)");
  }
  return out.join("\n");
}

function ConflictFileEditor({
  file,
  value,
  onText,
  choice,
  onChoice,
}: {
  file: ConflictFile;
  value: string;
  onText: (v: string) => void;
  choice?: "ours" | "theirs" | "delete";
  onChoice: (c: "ours" | "theirs" | "delete") => void;
}) {
  const segs = useMemo(() => parseSegments(file.merged ?? ""), [file.merged]);
  const total = segs.filter((s) => s.kind === "conflict").length;
  const [choices, setChoices] = useState<(HunkChoice | undefined)[]>(() => Array(total).fill(undefined));
  const [manual, setManual] = useState(false);
  const resolved = choices.filter(Boolean).length;

  function pick(h: number, c: HunkChoice) {
    const next = choices.slice();
    next[h] = next[h] === c ? undefined : c; // re-click toggles back to undecided
    setChoices(next);
    onText(buildResolved(segs, next));
  }

  // Binary / structural conflict — can't text-merge, so pick a whole side.
  if (file.status !== "conflict") {
    return (
      <div className="rounded-xl border border-[var(--border)] bg-[var(--glass)] p-3">
        <div className="mb-2 flex items-center gap-2">
          <span className="rounded bg-red-500/15 px-1.5 py-0.5 text-[10px] font-medium text-red-600 dark:text-red-300">binary</span>
          <span className="font-mono text-sm text-text/80">{file.path}</span>
        </div>
        <div className="flex flex-wrap gap-2 text-xs">
          {(["ours", "theirs", "delete"] as const).map((c) => (
            <label key={c} className={`cursor-pointer rounded-lg border px-3 py-1.5 ${choice === c ? "border-primary bg-primary/10 text-text" : "border-[var(--border)] text-text/60"}`}>
              <input type="radio" name={`bin-${file.path}`} className="hidden" checked={choice === c} onChange={() => onChoice(c)} />
              {c === "ours" ? "Keep platform" : c === "theirs" ? "Keep GitHub" : "Delete"}
            </label>
          ))}
        </div>
      </div>
    );
  }

  let hi = -1; // running conflict-hunk index across segments
  return (
    <div className="rounded-xl border border-[var(--border)] bg-[var(--glass)] p-3">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className="rounded bg-red-500/15 px-1.5 py-0.5 text-[10px] font-medium text-red-600 dark:text-red-300">conflict</span>
        <span className="font-mono text-sm text-text/80">{file.path}</span>
        <span className={`text-[11px] ${resolved === total ? "text-emerald-600 dark:text-emerald-300" : "text-amber-600 dark:text-amber-300"}`}>
          {resolved}/{total} resolved
        </span>
        <button onClick={() => setManual((m) => !m)} className="ml-auto text-[11px] text-text/45 underline hover:text-text">
          {manual ? "Back to per-conflict" : "Edit manually"}
        </button>
      </div>

      {manual ? (
        <textarea
          value={value}
          onChange={(e) => onText(e.target.value)}
          spellCheck={false}
          rows={Math.min(24, Math.max(8, value.split("\n").length + 1))}
          className="w-full resize-y rounded-lg border border-[var(--border)] bg-[var(--bg-subtle)] px-3 py-2 font-mono text-xs leading-relaxed text-text outline-none transition focus:border-primary"
        />
      ) : (
        <div className="max-h-[55vh] overflow-auto rounded-lg border border-[var(--border)] bg-[var(--bg-subtle)] font-mono text-xs leading-relaxed">
          {segs.map((s, idx) => {
            if (s.kind === "text")
              return <pre key={idx} className="whitespace-pre-wrap px-3 py-1 text-text/65">{s.lines.join("\n")}</pre>;
            hi += 1;
            const h = hi;
            return (
              <div key={idx} className="my-1 border-y border-red-500/20">
                <ConflictHunk ours={s.ours} theirs={s.theirs} choice={choices[h]} onPick={(c) => pick(h, c)} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ConflictHunk({ ours, theirs, choice, onPick }: { ours: string[]; theirs: string[]; choice?: HunkChoice; onPick: (c: HunkChoice) => void }) {
  if (choice) {
    const lines = choice === "ours" ? ours : choice === "theirs" ? theirs : [...ours, ...theirs];
    const label = choice === "ours" ? "Platform" : choice === "theirs" ? "GitHub" : "Both";
    return (
      <div className="bg-emerald-500/[0.05]">
        <div className="flex items-center justify-between px-3 py-1 text-[11px]">
          <span className="text-emerald-600 dark:text-emerald-300">✓ Accepted: {label}</span>
          <button onClick={() => onPick(choice)} className="text-text/40 underline hover:text-text">change</button>
        </div>
        <pre className="whitespace-pre-wrap px-3 pb-1 text-text/85">{lines.join("\n")}</pre>
      </div>
    );
  }
  return (
    <div>
      <div className="bg-amber-500/[0.06]">
        <div className="flex items-center justify-between px-3 py-1 text-[11px]">
          <span className="text-amber-600 dark:text-amber-300">Platform (ours)</span>
          <button onClick={() => onPick("ours")} className="rounded border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 font-medium text-amber-700 dark:text-amber-200 hover:bg-amber-500/20">Accept</button>
        </div>
        <pre className="whitespace-pre-wrap px-3 pb-1 text-text/80">{ours.join("\n")}</pre>
      </div>
      <div className="bg-blue-500/[0.06]">
        <div className="flex items-center justify-between px-3 py-1 text-[11px]">
          <span className="text-blue-600 dark:text-blue-300">GitHub (theirs)</span>
          <button onClick={() => onPick("theirs")} className="rounded border border-blue-500/40 bg-blue-500/10 px-2 py-0.5 font-medium text-blue-700 dark:text-blue-200 hover:bg-blue-500/20">Accept</button>
        </div>
        <pre className="whitespace-pre-wrap px-3 pb-1 text-text/80">{theirs.join("\n")}</pre>
      </div>
      <div className="px-3 py-1 text-center">
        <button onClick={() => onPick("both")} className="text-[11px] text-text/45 underline hover:text-text">Keep both</button>
      </div>
    </div>
  );
}
